<script setup>
import { computed } from 'vue';

const props = defineProps({
  classNum: { type: String, required: true },
  levelData: {
    type: Array,
    required: true,
    validator: (value) => value.every(item => item.level && typeof item.percentage === 'number' && typeof item.count === 'number'),
  },
  maxScale: { type: Number, default: 100 },
  chartHeight: { type: String, default: '480px' }
});

const getBarColor = (index) => { 
  const colors = [
    'var(--result-ai-primary-90)',
    'var(--result-ai-primary-80)',  
    'var(--result-ai-primary-70)',
    'var(--result-ai-primary-60)',
  ];
  return colors[index % colors.length];
};

const getBarHeightPercent = (value) => {
  const numericValue = Number(value);
  if (isNaN(numericValue) || props.maxScale === 0) return 0;
  return (numericValue / props.maxScale) * 100;
};

const getBarHeight = (value) => getBarHeightPercent(value) + '%';

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) return 'N';
  return value;
};

const gridLines = computed(() => {
  const lines = [];
  for (let i = 1; i < 11; i++) lines.push(i * 10);
  return lines;
});
</script>

<template>
  <div class="level-chart-wrapper">
    <div class="level-chart-container" :style="{height: chartHeight}">
      
      <div class="chart-y-axis" :style="{height: chartHeight}">        
        <div class="y-axis-label" v-for="n in 11" :key="n" :style="{ bottom: ((n - 1) * 10) + '%' }">
          {{ Math.round(props.maxScale / 10 * (n - 1)) }}%
        </div>
      </div>
      
      <div class="chart-bars-wrap">
        <div class="chart-grid-lines" :style="{height: chartHeight}">
          <p v-for="pos in gridLines" :key="pos" class="grid-line" :style="{ bottom: pos + '%' }"></p>
        </div>

        <div v-for="(item, index) in levelData" :key="index" class="bar-item-group"> 
            <div class="bar-item" :style="{height: chartHeight}"> 
                <div class="bar-top-value">
                    <span class="score-value">{{ getDisplayValue(item.percentage) }}%</span>    
                    <span class="count-value">({{ getDisplayValue(item.count) }}명)</span>             
                </div>
                <div class="bar" :style="{ height: getBarHeight(item.percentage), backgroundColor: getBarColor(index) }"></div>
            </div>
            <div class="group-label">{{ item.level }}</div>
        </div>
      </div>
    </div>   
  </div>
</template>

<style lang="scss" scoped>
.level-chart-wrapper { 
  padding: 40px 2rem 60px !important; 
  border: 1px solid var(--result-ai-border); 
  border-radius: 10px; 
}
.level-chart-container { 
  display: flex; 
  gap: 0; 
  width: 100%; 
  position: relative; 
  align-items: flex-start; 
}
.chart-y-axis { 
  position: relative; 
  width: 50px;
  z-index: 2; 
  font-size: 1rem; 
  font-weight: 600;
  display: flex; 
  padding-right: 1rem;
  flex-direction: column-reverse; 
  justify-content: space-between; 
  line-height: 10%; 
  text-align: left; 
}
.chart-bars-wrap { 
  display: flex; 
  flex: 1; 
  justify-content: space-around; 
  position: relative; 
}

.chart-grid-lines { 
  width: 100%; 
  position: absolute; 
  top: 0; left: 0; 
} 
.grid-line { 
  position: absolute; 
  top: 0; left: 0; 
  width: 100%; 
  border-bottom: 1px dashed #e0e0e0; 
}

.bar-item-group { 
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  position: relative; 
  width: 100px; 
  z-index: 4; 
}
.bar-item { 
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  justify-content: flex-end; 
  width: 100%; 
  padding: 0 20px; 
  position: relative; 
  height: 100%; 
  border-bottom: 2px solid var(--result-ai-border); 
}
.bar-top-value { 
  text-align: center;
   padding-bottom: 10px; 
   font-weight: 600; 
   line-height: 1.2; 
   font-size: 1rem; 
  }

.bar { 
  width: 36px; 
  border-radius: 4px 4px 0 0; 
  position: relative; 
  min-height: 3px; 
}
.group-label { 
  font-size: 16px; 
  font-weight: 500; 
  padding: 5px 0; 
}
.chart-type { 
  border-radius: 5px; 
  text-align: center; 
  line-height: 28px; 
  font-size: 16px; 
  font-weight: 500; 
  margin-left: 60px; 
}
</style>